"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";

const navLinks = [
  { label: "Home", href: "/homepage" },
  { label: "Submit Product", href: "/product-submission" },
  { label: "Admin", href: "/admin-dashboard" },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (href: string) => pathname === href || pathname.startsWith(href);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/98 backdrop-blur-xl shadow-md border-b border-kenya-black/10"
          : "bg-white/95 backdrop-blur-md"
      }`}
    >
      {/* Kenya flag stripe at very top */}
      <div className="h-[4px] w-full flex">
        <div className="flex-1 bg-kenya-black" />
        <div className="flex-1 bg-kenya-red" />
        <div className="flex-1 bg-kenya-green" />
      </div>

      <div className="max-w-8xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2 sm:gap-3 group min-w-0">
          <AppLogo
            size={32}
            text="County Product Portal"
            className="group-hover:opacity-80 transition-opacity flex-shrink-0"
          />
          <div className="hidden sm:block min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.35em] transition-colors text-kenya-black/50 font-condensed truncate">
              Ministry of Tourism & Wildlife
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={`px-4 py-2.5 rounded-lg text-[0.875rem] font-semibold font-sans tracking-[0.02em] transition-all duration-200 ${
                isActive(link.href)
                  ? "bg-kenya-red text-white shadow-sm"
                  : "text-kenya-black hover:text-kenya-red hover:bg-kenya-red/8"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            href="/sign-up"
            className="text-[0.875rem] font-semibold font-sans tracking-[0.02em] transition-colors text-kenya-black hover:text-kenya-red"
          >
            Sign Up
          </Link>
          <Link
            href="/product-submission"
            className="flex items-center gap-2 bg-kenya-red text-white px-5 py-2.5 rounded-lg text-[0.875rem] font-bold font-sans tracking-[0.02em] hover:bg-kenya-red/90 transition-all shadow-kenya"
          >
            Submit Product
            <Icon name="ArrowRightIcon" size={15} />
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden flex flex-col gap-1.5 p-3 text-kenya-black -mr-1"
          aria-label="Toggle menu"
        >
          <span
            className={`block w-5 h-[2px] bg-current transition-all duration-300 ${
              menuOpen ? "rotate-45 translate-y-[7px]" : ""
            }`}
          />
          <span
            className={`block w-5 h-[2px] bg-current transition-all duration-300 ${
              menuOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block w-5 h-[2px] bg-current transition-all duration-300 ${
              menuOpen ? "-rotate-45 -translate-y-[7px]" : ""
            }`}
          />
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden bg-white border-t border-kenya-black/10 transition-all duration-300 overflow-hidden ${
          menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-3 rounded-lg text-[0.9375rem] font-semibold font-sans tracking-[0.02em] transition-colors ${
                isActive(link.href)
                  ? "bg-kenya-red text-white" :"text-kenya-black hover:text-kenya-red hover:bg-kenya-red/8"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-kenya-black/10 flex flex-col gap-2">
            <Link
              href="/sign-up"
              onClick={() => setMenuOpen(false)}
              className="px-4 py-3 rounded-lg text-[0.9375rem] font-semibold font-sans tracking-[0.02em] text-kenya-black hover:text-kenya-red hover:bg-kenya-red/8 transition-colors"
            >
              Sign Up
            </Link>
            <Link
              href="/product-submission"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 bg-kenya-red text-white px-5 py-3 rounded-lg text-[0.9375rem] font-bold font-sans"
            >
              Submit Product
              <Icon name="ArrowRightIcon" size={15} />
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}